/**
 * Self-service onboarding flow — step registry and route helpers.
 *
 * The flow is: /get-started (create account) → /get-started/setup/:stepId
 * (the "Create workspace" wizard). Steps are identified by stable slugs so
 * the current step survives refresh and browser back/forward.
 *
 * The trial step is deliberately LAST: it is the confirm action, which posts
 * the whole wizard payload and hands off to Stripe-hosted Checkout, so every
 * other step's answers have to be collected before it.
 */

export interface SelfServiceStep {
  /** Stable slug used in the URL (/get-started/setup/:stepId). */
  id: string;
  /** Label shown in the step sidebar. */
  label: string;
  /** Page title shown above the step content. */
  title: string;
  /** Optional subtitle below the title. */
  subtitle?: string;
}

export const SELF_SERVICE_STEPS: readonly SelfServiceStep[] = [
  {
    id: 'crew',
    label: 'Tell us about your crew',
    title: 'Tell us about your crew',
    subtitle: 'This shapes the workspace Ahoy builds for you.',
  },
  {
    id: 'data',
    label: 'Customer data today',
    title: 'Bring in your customer data',
    subtitle:
      'Pick everything that applies. Ahoy imports from all of them and merges duplicates.',
  },
  {
    id: 'stack',
    label: 'Connect your stack',
    title: 'Connect your stack',
    subtitle:
      'Connect the tools you already use. Ahoy works from everything you connect – the more it sees, the more it can prep for you.',
  },
  {
    id: 'team',
    label: 'Invite your team',
    title: 'Get your crew onboard',
    subtitle:
      'Teammates who show up often in your meetings are suggested below. Each seat is $99/month and billing starts only when they accept.',
  },
  {
    id: 'trial',
    label: 'Start free trial',
    title: 'Start your free trial',
    subtitle:
      "Startup plan. You won't be charged today – your card won't be charged until your trial ends. Cancel anytime before then.",
  },
];

export const TEAM_SIZE_OPTIONS = ['Just me', '2-10', '11-50', '50 +'] as const;

export type TeamSize = (typeof TEAM_SIZE_OPTIONS)[number];

export type BillingPeriod = 'monthly' | 'annual';

export const BILLING_OPTIONS: readonly {
  value: BillingPeriod;
  label: string;
}[] = [
  { value: 'monthly', label: 'Monthly • $99 per seat' },
  { value: 'annual', label: 'Annual • $79 per seat' },
];

export const DATA_SOURCE_OPTIONS = [
  'HubSpot',
  'Salesforce',
  'Lightfield',
  'Google sheets',
  'Excel / CSV',
  'Notion / Airtable',
] as const;

export interface RecorderOption {
  id: string;
  name: string;
  description: string;
  iconSrc: string;
}

export const RECORDER_OPTIONS: readonly RecorderOption[] = [
  {
    id: 'ahoy',
    name: 'Ahoy meeting recorder',
    description: 'Records, transcribes, and analyzes – nothing to connect',
    iconSrc: '/assets/self-service/icons/asset-20.svg',
  },
  {
    id: 'granola',
    name: 'Granola',
    description: 'Connected with your Google sign-in',
    iconSrc: '/assets/self-service/icons/clip-path-group.svg',
  },
  {
    id: 'fathom',
    name: 'Fathom',
    description: 'Connected with your Google sign-in',
    iconSrc: '/assets/self-service/icons/fathom.png',
  },
];

export const GET_STARTED_PATH = '/get-started';
export const SETUP_BASE_PATH = '/get-started/setup';

export function selfServiceStepPath(stepId: string): string {
  return `${SETUP_BASE_PATH}/${stepId}`;
}

export const FIRST_STEP_PATH = selfServiceStepPath(SELF_SERVICE_STEPS[0].id);

export function findStepIndex(stepId: string | undefined): number {
  if (!stepId) {
    return -1;
  }
  return SELF_SERVICE_STEPS.findIndex(step => step.id === stepId);
}
